/** Insurance companies reference data (spec §7.7). */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Pencil, Plus } from 'lucide-react';
import { insuranceCompanySchema, type InsuranceCompanyDto } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/misc';
import { AutoForm, type FieldDescriptor } from '@/components/form/auto-form';
import { EntityAvatar } from '@/components/common/entity-avatar';
import { EmptyState, PageHeader } from '@/components/common/page-header';
import { post, put } from '@/lib/api/client';
import { useInsuranceCompanies } from '@/lib/api/domains';
import { queryKeys } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useQueryClient } from '@tanstack/react-query';

export function InsurersPage() {
  const { t, money, number } = useI18n();
  const { can } = useAuth();
  const { data, isLoading } = useInsuranceCompanies(true);

  const [editing, setEditing] = React.useState<InsuranceCompanyDto | null>(null);
  const [creating, setCreating] = React.useState(false);

  return (
    <>
      <PageHeader
        title={t('company.title')}
        description={t('company.subtitle')}
        actions={
          can('reference:write') && (
            <Button onClick={() => setCreating(true)}>
              <Plus /> {t('action.create')}
            </Button>
          )
        }
      />

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-32" />
          ))}
        </div>
      ) : !data?.length ? (
        <Card>
          <CardContent>
            <EmptyState title={t('common.empty')} hint={t('company.subtitle')} />
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.map((company) => (
            <Card
              key={company.id}
              className={company.isActive ? 'hover-lift' : 'hover-lift opacity-60'}
            >
              <CardContent className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  {/* The whole identity block is the link to the carrier's workspace —
                      the edit pencil beside it stays a separate, smaller target. */}
                  <Link
                    to={`/insurers/${company.id}`}
                    className="flex min-w-0 items-center gap-2.5 rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
                  >
                    <EntityAvatar name={company.name} size="lg" />
                    <div className="min-w-0">
                      <p className="hover:text-primary truncate font-semibold transition-colors duration-150">
                        {company.name}
                      </p>
                      {company.code && <p className="text-muted-soft tabular text-xs">{company.code}</p>}
                    </div>
                  </Link>
                  <div className="flex shrink-0 items-center gap-1">
                    {!company.isActive && (
                      <Badge tone="neutral" size="sm">
                        {t('common.no')}
                      </Badge>
                    )}
                    {can('reference:write') && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setEditing(company)}
                        aria-label={t('action.edit')}
                      >
                        <Pencil />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 border-t pt-3">
                  <Stat label={t('company.policies')} value={number(company.policyCount ?? 0)} />
                  <Stat
                    label={t('company.defaultCommission')}
                    value={company.defaultCommissionPercentage ? `${company.defaultCommissionPercentage}%` : '—'}
                  />
                  <Stat
                    label={t('company.settlementDays')}
                    value={company.settlementDays ? `${company.settlementDays}` : '—'}
                  />
                </div>

                {(company.contactPhone || company.contactEmail) && (
                  <div className="text-muted-foreground space-y-0.5 border-t pt-2 text-xs">
                    {company.contactPhone && <p className="tabular">{company.contactPhone}</p>}
                    {company.contactEmail && <p className="truncate">{company.contactEmail}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <InsurerFormDialog
        open={creating || Boolean(editing)}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
        company={editing ?? undefined}
      />
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-[11px]">{label}</p>
      <p className="text-sm font-semibold tabular">{value}</p>
    </div>
  );
}

/** Exported so the insurer detail page reuses the exact same edit form. */
export function InsurerFormDialog({
  open,
  onOpenChange,
  company,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: InsuranceCompanyDto;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [saving, setSaving] = React.useState(false);
  const formId = 'insurer-form';

  const fields: FieldDescriptor<Record<string, unknown>>[] = [
    { name: 'name', type: 'text', label: t('company.name'), required: true, span: 2 },
    { name: 'code', type: 'text', label: t('company.code') },
    { name: 'defaultCommissionPercentage', type: 'percent', label: t('company.defaultCommission') },
    { name: 'contactPhone', type: 'tel', label: t('client.phone') },
    { name: 'contactEmail', type: 'email', label: t('client.email') },
    { name: 'settlementDays', type: 'number', label: t('company.settlementDays') },
    /*
     * The three client-facing lines, grouped after the agency's own contact and hinted on the
     * first so the distinction is stated where it is filled in rather than only where it is
     * read. Getting these two groups confused is how a client ends up ringing a commercial
     * desk at 2am.
     */
    { name: 'claimsPhone', type: 'tel', label: t('insurer.claimsPhone'), hint: t('insurer.clientFacingHint') },
    { name: 'roadsidePhone', type: 'tel', label: t('insurer.roadsidePhone') },
    { name: 'emergencyPhone', type: 'tel', label: t('insurer.emergencyPhone') },
    { name: 'portalUrl', type: 'text', label: t('insurer.portalUrl'), span: 2, placeholder: 'https://' },
    { name: 'isActive', type: 'switch', label: t('user.active') },
    { name: 'notes', type: 'textarea', label: t('common.notes') },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{company ? t('action.edit') : t('company.one')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <AutoForm
            id={formId}
            schema={company ? insuranceCompanySchema.partial() : insuranceCompanySchema}
            defaultValues={
              {
                name: company?.name ?? '',
                code: company?.code ?? '',
                contactPhone: company?.contactPhone ?? '',
                contactEmail: company?.contactEmail ?? '',
                claimsPhone: company?.claimsPhone ?? '',
                roadsidePhone: company?.roadsidePhone ?? '',
                emergencyPhone: company?.emergencyPhone ?? '',
                portalUrl: company?.portalUrl ?? '',
                defaultCommissionPercentage: company?.defaultCommissionPercentage ?? '',
                settlementDays: company?.settlementDays ?? 45,
                isActive: company?.isActive ?? true,
                notes: company?.notes ?? '',
              } as never
            }
            fields={fields}
            onSubmit={async (values) => {
              setSaving(true);
              try {
                const payload = { ...values };
                // Empty strings are dropped rather than sent: the schema treats '' as neither a
                // value nor absent, and `portalUrl` in particular would fail its URL check.
                for (const key of [
                  'code',
                  'contactPhone',
                  'contactEmail',
                  'claimsPhone',
                  'roadsidePhone',
                  'emergencyPhone',
                  'portalUrl',
                  'defaultCommissionPercentage',
                ] as const) {
                  if (!payload[key]) delete payload[key];
                }

                if (company) await put(`/insurance-companies/${company.id}`, payload);
                else await post('/insurance-companies', payload);

                await queryClient.invalidateQueries({
                  queryKey: queryKeys.resource('insurance-companies'),
                });
                toast.success(t('common.saved'));
                onOpenChange(false);
              } finally {
                setSaving(false);
              }
            }}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button type="submit" form={formId} loading={saving}>
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
